/**
 * StatePlaces — reusable "cities in this region" grid.
 *
 * Mounts on any location article whose `kind` is `country` or `state`.
 * Fetches every child location via /api/locations-children/{slug} and
 * renders a searchable card grid plus a lightweight coordinates-based
 * map (OpenStreetMap iframe). Each card links to `/l/{child.slug}`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Search, Loader2,
  Landmark, Building2, Home, Trees, Globe2,
} from "lucide-react";
import AiTopicContent from "@/components/AiTopicContent";
import useAiTopic from "@/hooks/useAiTopic";
import PlaceStamp from "@/components/PlaceStamp";
import PlaceKindTag from "@/components/PlaceKindTag";

const API = process.env.REACT_APP_BACKEND_URL;

// Per-kind visual identity — colour + icon are picked up by both the
// legend chips and the individual place cards so users can scan a
// state landing at a glance ("orange = state, red = city…").
const KIND_STYLE = {
  country:  { color: "#0F172A", bg: "#F1F5F9", ring: "#CBD5E1", Icon: Globe2 },
  state:    { color: "#B45309", bg: "#FFF7ED", ring: "#FED7AA", Icon: Landmark },
  district: { color: "#7C3AED", bg: "#F5F3FF", ring: "#DDD6FE", Icon: Landmark },
  county:   { color: "#0E7490", bg: "#ECFEFF", ring: "#A5F3FC", Icon: Landmark },
  city:     { color: "#B91C1C", bg: "#FEF2F2", ring: "#FECACA", Icon: Building2 },
  town:     { color: "#1D4ED8", bg: "#EFF6FF", ring: "#BFDBFE", Icon: Home },
  village:  { color: "#15803D", bg: "#F0FDF4", ring: "#BBF7D0", Icon: Trees },
};

function styleFor(kind) {
  return KIND_STYLE[(kind || "").toLowerCase()] || {
    color: "#334155", bg: "#F8FAFC", ring: "#E2E8F0", Icon: MapPin,
  };
}

function bbox(items) {
  const lats = items.map((i) => i.lat).filter(Number.isFinite);
  const lngs = items.map((i) => i.lng).filter(Number.isFinite);
  if (!lats.length || !lngs.length) return null;
  const pad = 0.4;
  return `${Math.min(...lngs) - pad},${Math.min(...lats) - pad},${Math.max(...lngs) + pad},${Math.max(...lats) + pad}`;
}

export default function StatePlaces({ slug, name }) {
  const [items, setItems] = useState([]);
  const [loading, setLoad] = useState(true);
  const [q, setQ]        = useState("");

  // Auto-generated regional narrative — mirrors the AI Topics engine
  // used everywhere else on the site. `source=location` + entity_key
  // `<slug>|overview` gives us a stable slug that we can regenerate
  // on demand as coverage improves.
  const overview = useAiTopic({
    source:    "location",
    entityKey: slug ? `${slug}|overview` : "",
    title:     name ? `${name} — Overview` : "State Overview",
    context:   `Write a friendly, evergreen overview of ${name}. Cover: what the region is known for, geography and climate, culture and languages, historical highlights, and notable cities or attractions. Keep it welcoming — this is the landing page for readers exploring the region for the first time.`,
    deepLink:  slug ? `/l/${slug}` : null,
    enabled:   !!slug,
  });

  useEffect(() => {
    let alive = true;
    if (!slug) return;
    axios.get(`${API}/api/locations-children/${slug}`)
      .then((r) => alive && setItems(r.data?.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoad(false));
    return () => { alive = false; };
  }, [slug]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((i) => (i.name || "").toLowerCase().includes(term) ||
                               (i.slug || "").toLowerCase().includes(term));
  }, [items, q]);

  // Counties and cities are rendered in two separate grids when both
  // populations exist — keeps the state landing scannable even after
  // the nightly OSM county sweep fills in 60+ rows per US state.
  const counties = filtered.filter((i) => (i.kind || "").toLowerCase() === "county");
  const cities = filtered.filter((i) => {
    const k = (i.kind || "").toLowerCase();
    return k === "city" || k === "town" || k === "village";
  });
  const otherPlaces = filtered.filter((i) => {
    const k = (i.kind || "").toLowerCase();
    return k !== "county" && k !== "city" && k !== "town" && k !== "village";
  });

  const withCoords = filtered.filter((i) => Number.isFinite(i.lat) && Number.isFinite(i.lng));
  const mapBbox    = bbox(withCoords);

  // Three drill-down CTAs pinned above the grid — the "State Panel"
  // requested by the product owner. Anchors scroll to the relevant
  // grids further down the page; "All about" opens the standalone
  // overview topic so readers who just want to read the profile can
  // skip the directory entirely.
  const panelLinks = [
    counties.length > 0 && {
      href: "#state-counties",
      label: `Explore ${name} Counties`,
      count: counties.length,
      style: KIND_STYLE.county,
      testid: "state-panel-counties",
    },
    cities.length > 0 && {
      href: "#state-cities",
      label: `Explore ${name} Cities`,
      count: cities.length,
      style: KIND_STYLE.city,
      testid: "state-panel-cities",
    },
    slug && {
      href: `/lt/${slug}/topic/overview`,
      label: `All about ${name}`,
      count: null,
      style: KIND_STYLE.state,
      testid: "state-panel-about",
      isLink: true,
    },
  ].filter(Boolean);

  return (
    <section className="mt-12" data-testid="state-places">
      {/* Regional narrative — auto-generated on first view. */}
      <div className="mb-8" data-testid="state-overview">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4" />
          <h2 className="font-display text-2xl">About {name}</h2>
        </div>
        <AiTopicContent
          doc={overview.doc}
          status={overview.status}
          retry={overview.retry}
          title={`${name} — Overview`}
        />
      </div>

      {/* State Panel — colourful CTAs for Counties · Cities · All about. */}
      {panelLinks.length > 0 && (
        <div
          className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="state-panel"
        >
          {panelLinks.map((p) => {
            const Icon = p.style.Icon;
            const inner = (
              <>
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 shadow-sm"
                  style={{ background: "#fff", color: p.style.color, border: `2px solid ${p.style.ring}` }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base leading-tight truncate" style={{ color: p.style.color }}>
                    {p.label}
                  </div>
                  <div className="text-xs opacity-70 mt-0.5">
                    {p.count != null ? `${p.count} destinations` : "Read the full profile"}
                  </div>
                </div>
                <span className="text-lg opacity-60 group-hover:opacity-100 transition" aria-hidden>→</span>
              </>
            );
            const cls = "group flex items-center gap-3 rounded-2xl border-2 px-4 py-3 transition hover:shadow-md hover:-translate-y-0.5";
            const inline = { borderColor: p.style.ring, background: p.style.bg };
            return p.isLink ? (
              <Link key={p.testid} to={p.href} className={cls} style={inline} data-testid={p.testid}>
                {inner}
              </Link>
            ) : (
              <a key={p.testid} href={p.href} className={cls} style={inline} data-testid={p.testid}>
                {inner}
              </a>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4" />
        <h2 className="font-display text-2xl">Places in {name}</h2>
        <span className="ml-2 text-xs font-mono opacity-60">
          {loading ? "…" : `${filtered.length} of ${items.length}`}
        </span>
      </div>

      {loading && (
        <div className="text-sm opacity-60 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading places…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-sm opacity-70 rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
          No places seeded yet. Our Geographic Discovery Engine will populate this shortly.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="mb-3 flex items-center gap-2">
            <Search className="w-4 h-4 opacity-60" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search places in ${name}`}
              className="flex-1 text-sm rounded-lg border px-3 py-2 bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="state-places-search"
            />
          </div>

          {mapBbox && (
            <div
              className="rounded-2xl overflow-hidden border mb-4"
              style={{ borderColor: "var(--wofy-border)", aspectRatio: "16/9" }}
              data-testid="state-places-map"
            >
              <iframe
                title={`Map of ${name}`}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapBbox}&layer=mapnik`}
                className="w-full h-full block"
                loading="lazy"
              />
            </div>
          )}

          {/* Cities / towns / villages — get their own grid with vibrant
              per-kind styling so state landings show the "main" places
              first, ahead of the denser county grid below. */}
          {cities.length > 0 && (
            <div id="state-cities" className="scroll-mt-20" data-testid="state-cities">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4" style={{ color: KIND_STYLE.city.color }} />
                <h3 className="font-display text-xl">Cities & Towns in {name}</h3>
                <span className="ml-2 text-xs font-mono opacity-60">{cities.length}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cities.map((c) => <PlaceCard key={c.slug} place={c} />)}
              </div>
            </div>
          )}

          {otherPlaces.length > 0 && (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="state-other-places">
              {otherPlaces.map((c) => <PlaceCard key={c.slug} place={c} />)}
            </div>
          )}

          {/* Counties — populated by the nightly OSM sweep. Renders a
              distinct denser grid so state landing pages don't blow up
              once every US state pulls 60-250 counties. */}
          {counties.length > 0 && (
            <div id="state-counties" className="mt-10 scroll-mt-20" data-testid="state-counties">
              <div className="flex items-center gap-2 mb-3">
                <Landmark className="w-4 h-4" style={{ color: KIND_STYLE.county.color }} />
                <h3 className="font-display text-xl">Counties in {name}</h3>
                <span className="ml-2 text-xs font-mono opacity-60">{counties.length}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {counties.map((c) => {
                  const s = styleFor(c.kind);
                  return (
                    <Link
                      key={c.slug}
                      to={`/l/${c.slug}`}
                      className="rounded-lg border-2 px-2 py-2 text-sm hover:shadow-md hover:-translate-y-0.5 transition truncate flex items-center gap-2"
                      style={{ borderColor: s.ring, background: s.bg }}
                      data-testid={`state-county-${c.slug}`}
                    >
                      <PlaceStamp kind="county" slug={c.slug} override={c.stamp_url} size={28} className="shrink-0" title={c.name} />
                      <span className="truncate block" style={{ color: s.color }}>{c.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * PlaceCard — vibrant per-kind card used across the state landing.
 * Colour + icon come from KIND_STYLE so every card scans instantly as
 * a city / town / village / district.
 */
function PlaceCard({ place }) {
  const s = styleFor(place.kind);
  return (
    <Link
      to={`/l/${place.slug}`}
      className="group rounded-xl border-2 p-3 hover:shadow-md hover:-translate-y-0.5 transition flex items-start gap-3"
      style={{ borderColor: s.ring, background: s.bg }}
      data-testid={`state-place-${place.slug}`}
    >
      <PlaceStamp
        kind={place.kind}
        slug={place.slug}
        override={place.stamp_url}
        size={44}
        className="shadow-sm ring-1 ring-white/60"
        title={place.name}
      />
      <div className="min-w-0 flex-1">
        <div className="mt-0.5 font-medium group-hover:underline truncate" style={{ color: s.color }}>
          {place.name}
        </div>
        <div className="mt-1">
          <PlaceKindTag kind={place.kind} slug={place.slug} country={place.country} asLink />
        </div>
        {(place.state || place.country) && (
          <div className="text-xs opacity-70 mt-0.5 truncate">
            {[place.state, place.country].filter(Boolean).join(" · ")}
          </div>
        )}
        {place.capital && (
          <div className="text-xs opacity-70 mt-0.5">Capital: {place.capital}</div>
        )}
      </div>
    </Link>
  );
}
